import { Middleware, isRejected } from '@reduxjs/toolkit';
import { getDialogMessageIfKnown, logError } from '@/utils/errorLogger';
import { showError } from '@/store/slices/globalErrorSlice';
import { HTTP_STATUS } from '@/consts';

/**
 * Redux middleware that logs all rejected async thunk actions to console.
 * Also shows an error dialog for known error types.
 *
 * Note: createAsyncThunk serializes thrown errors into plain objects, so
 * statusCode checks work on both live ApiError instances and serialized objects.
 * 401/500 errors are skipped since apiFetch already handles these specially
 * (401: redirects to login, 500: navigates to /error page).
 */
export const errorLoggingMiddleware: Middleware =
  ({ dispatch }) =>
  (next) =>
  (action: unknown) => {
    if (isRejected(action)) {
      const error = action.payload ?? action.error;
      // Skip logging 401/500 errors — they're already handled by apiFetch
      const hasSpecialStatus =
        typeof error === 'object' &&
        error !== null &&
        'statusCode' in error &&
        (error.statusCode === HTTP_STATUS.UNAUTHORIZED ||
          error.statusCode === HTTP_STATUS.INTERNAL_SERVER_ERROR);

      if (!hasSpecialStatus) {
        logError(error);

        const dialogMessage = getDialogMessageIfKnown(error);
        if (dialogMessage !== null) {
          dispatch(showError({ message: dialogMessage, statusCode: null }));
        }
      }
    }

    return next(action);
  };
